import { useMemo, useState } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import CancelRecordModal from 'components/modals/CancelRecordModal';
import ConfirmPatientRemovalModal from 'components/modals/ConfirmPatientRemovalModal';
import DischargeModal from 'components/modals/DischargeModal';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import DividerSeries from 'components/shared/DividerSeries';
import Menu from 'components/shared/menu';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import useWebsocketExport from 'hooks/useWebsocketExport';
import Episode, { EpisodeAccessLevel } from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS } from 'models/Profile';
import { createEpisodeExport } from 'services/api/episode';
import colors from 'styles/theme/colors';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';
import DownloadIcon from 'svg/DownloadIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';
import TrashIcon from 'svg/TrashIcon';

type Props = {
  episode: Episode;
  invalidateData: () => void;
};

export default function PatientActionsMenu({ episode, invalidateData }: Props) {
  const { profile } = useProfile();
  const [showEditDischargeModal, setShowEditDischargeModal] = useState(false);
  const [showCancelRecord, setShowCancelRecord] = useState(false);
  const [showRemovePatientModal, setShowRemovePatientModal] = useState(false);
  const navigate = useNavigate();
  const { locationEpisode } = useSelectedLocationEpisode();
  const { exporting, initiateExport } = useWebsocketExport(createEpisodeExport);
  const { translate } = useTranslation();

  const latestLocationEpisode = episode.latestLocationEpisode;

  const locationEpisodeForEditDischarge = new LocationEpisode({
    id: episode.latestLocationEpisodeDischarge?.locationEpisodeId,
  });

  // We only allow cancelation of the LATEST location episode
  // as long as it is not the only one and it is not archived or discharged
  const locationEpisodeEligibleForCancelation =
    episode.locationEpisodes.length > 1 && !latestLocationEpisode?.archived && !latestLocationEpisode?.discharged;

  const actions: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    show?: boolean;
    color?: string;
  }[][] = useMemo(() => {
    return episode.archived
      ? []
      : [
          [
            {
              icon: <PencilIcon color={'transparent'} stroke={colors.black100} strokeWidth={1.5} />,
              label: 'Edit patient details',
              onClick: () => navigate(`/patients/${episode?.id}/edit`),
              show:
                profile.has(PERMISSIONS.patientEdit, { episode, locationEpisode }) &&
                episode.currentUserAccessLevel === EpisodeAccessLevel.FULL,
            },
            {
              icon: <DownloadIcon />,
              label: 'Download patient story',
              onClick: () => initiateExport(episode.id),
              show: true,
            },
          ],
          [
            {
              icon: <PencilIcon color={'transparent'} stroke={colors.black100} strokeWidth={1.5} />,
              label: `Edit ${translate('discharge').toLowerCase()} information`,
              onClick: () => setShowEditDischargeModal(true),
              show: profile.has(PERMISSIONS.rehabStateDischargeEdit) && !!episode.latestLocationEpisodeDischarge,
            },
            {
              icon: <CircleXIconOutlined width={14} height={14} />,
              label: `Cancel record at ${latestLocationEpisode?.group?.name}`,
              onClick: () => setShowCancelRecord(true),
              show:
                locationEpisodeEligibleForCancelation &&
                profile.has(PERMISSIONS.locationEpisodeDelete, { locationEpisode: latestLocationEpisode, episode }),
            },
          ],
          [
            {
              icon: <TrashIcon width={14} height={14} />,
              label: 'Remove patient from Olio',
              onClick: () => setShowRemovePatientModal(true),
              color: colors.red100,
              show: profile.has(PERMISSIONS.patientDelete, { episode }),
            },
          ],
        ];
  }, [
    episode,
    profile,
    locationEpisode,
    latestLocationEpisode,
    locationEpisodeEligibleForCancelation,
    navigate,
    initiateExport,
    translate,
  ]);

  const showMenu = actions.some((section) => section.some((action) => action.show));

  return (
    <>
      {showMenu && (
        <Menu $size='large'>
          <Menu.Trigger>
            <Button
              variant='outline-gray'
              style={{ width: 44, padding: 0, height: 44, flexShrink: 0 }}
              data-cy='patientActionsMenuButton'>
              {exporting ? <ExportLoadingIndicator thickness={2} /> : <EllipsisIcon />}
            </Button>
          </Menu.Trigger>
          <Menu.Content position='right' style={{ marginTop: 4 }}>
            <DividerSeries divider={<Menu.Divider />}>
              {actions.map((section, index) => (
                <React.Fragment key={index}>
                  {section
                    .filter((action) => action.show)
                    .map((action, i) => (
                      <StyledMenuItem
                        data-cy='flyout-menu'
                        style={{ color: action.color }}
                        key={i}
                        onClick={action.onClick}>
                        {action.icon}
                        {action.label}
                      </StyledMenuItem>
                    ))}
                </React.Fragment>
              ))}
            </DividerSeries>
          </Menu.Content>
        </Menu>
      )}

      {showEditDischargeModal && (
        <DischargeModal
          episode={episode}
          locationEpisode={locationEpisodeForEditDischarge}
          setShow={setShowEditDischargeModal}
          patientName={episode.patient.name}
          invalidateData={invalidateData}
        />
      )}

      {showCancelRecord && latestLocationEpisode && (
        <CancelRecordModal
          locationEpisodeId={latestLocationEpisode.id}
          patientName={episode.patient.name}
          groupName={latestLocationEpisode.group?.name || ''}
          onCancel={() => setShowCancelRecord(false)}
          onSuccess={() => setShowCancelRecord(false)}
        />
      )}

      {showRemovePatientModal && (
        <ConfirmPatientRemovalModal
          episodeId={episode.id}
          patientName={episode.patient.name}
          onCancel={() => setShowRemovePatientModal(false)}
        />
      )}
    </>
  );
}

const StyledMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const ExportLoadingIndicator = styled(CircularProgress)`
  width: 18px;
  height: 18px;
  color: ${colors.blue100};
`;
